/**
 * Custom error class for timeout-related failures
 */
export class TimeoutError extends Error {
  constructor(message, operation) {
    super(message);
    this.name = 'TimeoutError';
    this.operation = operation;
  }
}

/**
 * Wraps a promise with a timeout. If the promise doesn't resolve/reject
 * within the specified time, it will reject with a TimeoutError.
 *
 * @param {Promise} promise - The promise to wrap
 * @param {number} ms - Timeout in milliseconds
 * @param {string} operation - Description of the operation for error messages
 * @returns {Promise} - Promise that resolves/rejects with timeout protection
 *
 * @example
 * const result = await withTimeout(
 *   fetch('/api/data'),
 *   5000,
 *   'API fetch'
 * );
 */
export function withTimeout(promise, ms, operation = 'Operation') {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new TimeoutError(`${operation} timed out after ${ms}ms`, operation));
    }, ms);

    promise
      .then((result) => {
        clearTimeout(timeoutId);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
      });
  });
}

/**
 * Creates a promise with external resolve/reject controls and built-in timeout.
 * Useful for wrapping callback-based APIs that need timeout protection.
 *
 * @param {number} ms - Timeout in milliseconds
 * @param {string} operation - Description of the operation for error messages
 * @returns {Object} - Object with { promise, resolve, reject, clear }
 *
 * @example
 * const { promise, resolve, reject, clear } = createTimeoutPromise(15000, 'User profile fetch');
 * Fire.getUser(uid, true, (json) => {
 *   if (json.error) {
 *     reject(new Error(json.error));
 *   } else {
 *     resolve(json);
 *   }
 * });
 * const result = await promise;
 */
export function createTimeoutPromise(ms, operation = 'Operation') {
  let resolveCallback;
  let rejectCallback;
  let timeoutId;

  const promise = new Promise((resolve, reject) => {
    resolveCallback = resolve;
    rejectCallback = reject;

    timeoutId = setTimeout(() => {
      reject(new TimeoutError(`${operation} timed out after ${ms}ms`, operation));
    }, ms);
  });

  // Clear the timeout when promise resolves/rejects
  promise.then(
    () => clearTimeout(timeoutId),
    () => clearTimeout(timeoutId)
  );

  return {
    promise,
    resolve: (value) => {
      clearTimeout(timeoutId);
      resolveCallback(value);
    },
    reject: (error) => {
      clearTimeout(timeoutId);
      rejectCallback(error);
    },
    clear: () => clearTimeout(timeoutId),
  };
}
